/**
 * IdeaOverviewEditor - Editable cards for idea spec sections
 *
 * Renders each idea_spec section as an editable card with:
 * - View mode: Display current value with Edit button
 * - Edit mode: Inline editor with Save/Cancel
 * - Triggers cascade analysis on save
 */
import React, { useState } from 'react';
import {
  Button,
  Text,
  Caption,
  tokens,
} from '../design-system';
import { FaEdit, FaTimes, FaCheck, FaPlus, FaTrash } from 'react-icons/fa';
import { useAnalyzeIdeaEdit, useApplyIdeaEdit } from '../hooks/api/useIdeaEdit';
import { useApp } from '../contexts/AppContext';
import IdeaCascadeModal from './IdeaCascadeModal';

// Section configuration
const SECTIONS = {
  executive_summary: {
    label: 'Executive Summary',
    type: 'text',
    description: 'High-level overview of the idea',
  },
  problem_statement: {
    label: 'Problem Statement',
    type: 'text',
    description: 'The problem this idea solves',
  },
  target_users: {
    label: 'Target Users',
    type: 'personas',
    description: 'Who this idea is for',
  },
  user_journeys: {
    label: 'User Journeys',
    type: 'journeys',
    description: 'Key user flows through the app',
  },
  constraints: {
    label: 'Constraints',
    type: 'list',
    description: 'Technical and business constraints',
  },
  success_metrics: {
    label: 'Success Metrics',
    type: 'list',
    description: 'How success will be measured',
  },
  out_of_scope: {
    label: 'Out of Scope',
    type: 'list',
    description: 'What this idea will NOT include',
  },
  risks: {
    label: 'Risks',
    type: 'risks',
    description: 'Identified risks and mitigations',
  },
};

// Text Editor Component
const TextEditor = ({ value, onChange }) => (
  <textarea
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: '100%',
      minHeight: '120px',
      padding: tokens.spacing[3],
      backgroundColor: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: tokens.borderRadius.md,
      color: 'var(--color-text)',
      fontSize: tokens.typography.fontSize.sm[0],
      fontFamily: 'inherit',
      resize: 'vertical',
      outline: 'none',
    }}
  />
);

// List Editor Component
const ListEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];

  const handleAdd = () => {
    onChange([...items, '']);
  };

  const handleRemove = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleChange = (idx, newValue) => {
    const updated = [...items];
    updated[idx] = newValue;
    onChange(updated);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', gap: tokens.spacing[2] }}>
          <input
            type="text"
            value={item}
            onChange={(e) => handleChange(idx, e.target.value)}
            style={{
              flex: 1,
              padding: tokens.spacing[2],
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: tokens.borderRadius.md,
              color: 'var(--color-text)',
              fontSize: tokens.typography.fontSize.sm[0],
              outline: 'none',
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => handleRemove(idx)}
          >
            <FaTrash size={12} />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={handleAdd}
        style={{ alignSelf: 'flex-start' }}
      >
        <FaPlus size={12} style={{ marginRight: tokens.spacing[1] }} />
        Add Item
      </Button>
    </div>
  );
};

// Single Section Card
const SectionCard = ({ section, config, value, ideaId, onUpdate, containerStyle, renderDisplay, hideHeader = false, initialEditing = false, onCancel }) => {
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [editValue, setEditValue] = useState(value);
  const [showCascadeModal, setShowCascadeModal] = useState(false);
  const [cascadeData, setCascadeData] = useState(null);

  const { showSuccess, showError } = useApp();
  const analyzeMutation = useAnalyzeIdeaEdit();
  const applyMutation = useApplyIdeaEdit();

  const handleEdit = () => {
    setEditValue(value);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    onCancel?.();
  };

  const handleSave = async () => {
    let valueToSave = editValue;
    // For complex types, ensure we send parsed JSON, not a raw string
    if (config.type !== 'text' && config.type !== 'list' && typeof valueToSave === 'string') {
      try {
        valueToSave = JSON.parse(valueToSave);
      } catch {
        showError('Invalid JSON format. Please check your input and try again.');
        return;
      }
    }
    try {
      // Analyze the change
      const result = await analyzeMutation.mutateAsync({
        ideaId,
        section,
        changes: { value: valueToSave },
      });
      // If there are cascades or affected plans, show modal
      if (result.cascade_changes?.length > 0 || result.affected_projects?.length > 0) {
        setCascadeData(result);
        setShowCascadeModal(true);
      } else {
        // No cascades, apply directly -- still pass version for locking
        setCascadeData(result);
        await applyChange([]);
      }
    } catch (error) {
      showError(error.message || 'Failed to analyze change');
    }
  };

  const applyChange = async (approvedCascades) => {
    try {
      await applyMutation.mutateAsync({
        ideaId,
        section,
        changes: { value: editValue },
        approvedCascades,
        expectedVersion: cascadeData?.current_version ?? null,
      });

      showSuccess(`${config.label} updated successfully`);
      setIsEditing(false);
      setShowCascadeModal(false);
      setCascadeData(null);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      showError(error.message || 'Failed to apply change');
    }
  };

  const renderValue = () => {
    if (!value) {
      return <Caption style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Not set</Caption>;
    }

    if (config.type === 'text') {
      return <Text style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</Text>;
    }

    if (config.type === 'list') {
      const items = Array.isArray(value) ? value : [];
      if (items.length === 0) {
        return <Caption style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No items</Caption>;
      }
      return (
        <ul style={{ margin: 0, paddingLeft: tokens.spacing[4] }}>
          {items.map((item, idx) => (
            <li key={idx}>
              <Text style={{ margin: 0 }}>{item}</Text>
            </li>
          ))}
        </ul>
      );
    }

    if (config.type === 'personas') {
      const personas = Array.isArray(value) ? value : [];
      if (personas.length === 0) {
        return <Caption style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No personas defined</Caption>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[3] }}>
          {personas.map((persona, idx) => (
            <div key={idx} style={{
              padding: tokens.spacing[3],
              backgroundColor: 'var(--color-surface)',
              borderRadius: tokens.borderRadius.md,
              border: '1px solid var(--color-border)',
            }}>
              <Text style={{ fontWeight: tokens.typography.fontWeight.medium, margin: 0 }}>
                {persona.persona || persona.name || `Persona ${idx + 1}`}
              </Text>
              {persona.description && (
                <Caption style={{ display: 'block', marginTop: tokens.spacing[1] }}>
                  {persona.description}
                </Caption>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (config.type === 'journeys') {
      const journeys = Array.isArray(value) ? value : [];
      if (journeys.length === 0) {
        return <Caption style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No journeys defined</Caption>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[3] }}>
          {journeys.map((journey, idx) => (
            <div key={idx} style={{
              padding: tokens.spacing[3],
              backgroundColor: 'var(--color-surface)',
              borderRadius: tokens.borderRadius.md,
              border: '1px solid var(--color-border)',
            }}>
              <Text style={{ fontWeight: tokens.typography.fontWeight.medium, margin: 0 }}>
                {journey.name || `Journey ${idx + 1}`}
              </Text>
              {journey.steps && (
                <ol style={{ margin: `${tokens.spacing[2]} 0 0 0`, paddingLeft: tokens.spacing[4] }}>
                  {journey.steps.map((step, sIdx) => (
                    <li key={sIdx}><Caption>{step}</Caption></li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (config.type === 'risks') {
      const risks = Array.isArray(value) ? value : [];
      if (risks.length === 0) {
        return <Caption style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No risks identified</Caption>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
          {risks.map((risk, idx) => (
            <div key={idx} style={{
              padding: tokens.spacing[3],
              backgroundColor: 'var(--color-surface)',
              borderRadius: tokens.borderRadius.md,
              border: '1px solid var(--color-border)',
            }}>
              <Text style={{ fontWeight: tokens.typography.fontWeight.medium, margin: 0 }}>
                {risk.description || risk.risk || `Risk ${idx + 1}`}
              </Text>
              {risk.mitigation && (
                <Caption style={{ display: 'block', marginTop: tokens.spacing[1], color: 'var(--color-text-muted)' }}>
                  Mitigation: {risk.mitigation}
                </Caption>
              )}
            </div>
          ))}
        </div>
      );
    }

    return <Text style={{ margin: 0 }}>{JSON.stringify(value)}</Text>;
  };

  const renderEditor = () => {
    if (config.type === 'text') {
      return <TextEditor value={editValue} onChange={setEditValue} />;
    }

    if (config.type === 'list') {
      return <ListEditor value={editValue} onChange={setEditValue} />;
    }

    // For complex types, fall back to JSON editing for now
    return (
      <textarea
        value={typeof editValue === 'string' ? editValue : JSON.stringify(editValue, null, 2)}
        onChange={(e) => {
          try {
            setEditValue(JSON.parse(e.target.value));
          } catch {
            setEditValue(e.target.value);
          }
        }}
        style={{
          width: '100%',
          minHeight: '200px',
          padding: tokens.spacing[3],
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: tokens.borderRadius.md,
          color: 'var(--color-text)',
          fontSize: tokens.typography.fontSize.sm[0],
          fontFamily: 'monospace',
          resize: 'vertical',
          outline: 'none',
        }}
      />
    );
  };

  return (
    <>
      <div style={{
        padding: tokens.spacing[4],
        backgroundColor: 'var(--color-background)',
        borderRadius: tokens.borderRadius.lg,
        border: '1px solid var(--color-border)',
        marginBottom: tokens.spacing[4],
        ...containerStyle,
      }}>
        {!hideHeader && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: tokens.spacing[3],
          }}>
            <div>
              <Text style={{
                fontWeight: tokens.typography.fontWeight.semibold,
                margin: 0,
              }}>
                {config.label}
              </Text>
              <Caption style={{ display: 'block', marginTop: tokens.spacing[1] }}>
                {config.description}
              </Caption>
            </div>

            {!isEditing && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleEdit}
              >
                <FaEdit size={14} style={{ marginRight: tokens.spacing[1] }} />
                Edit
              </Button>
            )}
          </div>
        )}

        {isEditing ? (
          <div>
            {renderEditor()}
            <div style={{
              display: 'flex',
              gap: tokens.spacing[2],
              marginTop: tokens.spacing[3],
              justifyContent: 'flex-end',
            }}>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                disabled={analyzeMutation.isPending || applyMutation.isPending}
              >
                <FaTimes size={12} style={{ marginRight: tokens.spacing[1] }} />
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="primary"
                onClick={handleSave}
                loading={analyzeMutation.isPending || applyMutation.isPending}
                disabled={analyzeMutation.isPending || applyMutation.isPending}
              >
                <FaCheck size={12} style={{ marginRight: tokens.spacing[1] }} />
                Save
              </Button>
            </div>
          </div>
        ) : (
          renderDisplay ? renderDisplay(value) : renderValue()
        )}
      </div>

      {/* Cascade Modal */}
      <IdeaCascadeModal
        isOpen={showCascadeModal}
        onClose={() => {
          setShowCascadeModal(false);
          setCascadeData(null);
        }}
        directChange={cascadeData?.direct_change}
        cascadeChanges={cascadeData?.cascade_changes || []}
        affectedPlans={cascadeData?.affected_projects || []}
        summary={cascadeData?.summary}
        warnings={cascadeData?.warnings || []}
        onApply={applyChange}
        isApplying={applyMutation.isPending}
      />
    </>
  );
};

// Main Component
const IdeaOverviewEditor = ({ idea, onUpdate }) => {
  if (!idea?.idea_spec) {
    return (
      <div style={{ padding: tokens.spacing[4], textAlign: 'center' }}>
        <Caption>No idea specification available</Caption>
      </div>
    );
  }

  return (
    <div>
      {Object.entries(SECTIONS).map(([section, config]) => (
        <SectionCard
          key={section}
          section={section}
          config={config}
          value={idea.idea_spec[section]}
          ideaId={idea.id}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  );
};

export { SectionCard, SECTIONS };
export default IdeaOverviewEditor;
